/**
 * Requirement Calculator - DCC Curve Interpolation
 * 
 * This module implements the MATLAB "My Power" explorer logic for battery cells.
 * It calculates achievable energy/power combinations at user-defined operating points
 * using discharge constant current (DCC) curves at 25°C (298.15K) ambient temperature.
 * 
 * MATLAB Reference:
 * - Given a desired power density (my_P_grav), interpolate achievable energy density
 * - Given a desired energy density (my_W_grav), interpolate achievable power density
 * - Downward extrapolation is ALLOWED (when requirement < min of curve)
 *   → Cells don't disappear when requirements are made smaller
 * - Upward extrapolation is NOT ALLOWED (when requirement > max of curve)
 *   → Cells disappear when they can't meet the requirements
 * 
 * Background:
 * DCC curves show the relationship between power and energy at constant current discharge.
 * For a given power requirement, the curve tells us how much energy the cell can deliver.
 * For a given energy requirement, the curve tells us at what power this can be achieved.
 */

/**
 * Linear interpolation helper with optional downward extrapolation
 * 
 * MATLAB Reference Logic:
 * - Extrapolation is allowed ONLY for values BELOW the minimum (downward)
 * - Extrapolation is NOT allowed for values ABOVE the maximum (upward)
 * - Reason: When requirements are small, cells shouldn't disappear
 *           When requirements exceed cell capability, cells should disappear
 * 
 * @param {number[]} x - Independent variable array (must be monotonic)
 * @param {number[]} y - Dependent variable array
 * @param {number} xi - Interpolation point
 * @param {boolean} allowDownwardExtrapolation - If true, extrapolate when xi < xMin (default: false)
 * @returns {number|null} Interpolated value, or null if extrapolation would be required
 */
function linearInterpolate(x, y, xi, allowDownwardExtrapolation = false) {
  if (!Array.isArray(x) || !Array.isArray(y) || x.length !== y.length || x.length < 2) {
    return null
  }
  
  const xMin = Math.min(...x)
  const xMax = Math.max(...x)
  
  // Check bounds - allow downward extrapolation if enabled
  if (xi > xMax) {
    // Above maximum: NEVER extrapolate (cell doesn't meet requirements)
    return null
  }
  
  if (xi < xMin) {
    if (!allowDownwardExtrapolation) {
      return null
    }
    // Downward extrapolation: use first two points for linear extrapolation
    // Sort to find the two smallest x values
    const sortedIndices = x.map((_, idx) => idx).sort((a, b) => x[a] - x[b])
    const i1 = sortedIndices[0]
    const i2 = sortedIndices[1]
    
    const x1 = x[i1]
    const x2 = x[i2]
    const y1 = y[i1]
    const y2 = y[i2]
    
    if (typeof x1 !== 'number' || isNaN(x1) || !isFinite(x1) ||
        typeof x2 !== 'number' || isNaN(x2) || !isFinite(x2) ||
        typeof y1 !== 'number' || isNaN(y1) || !isFinite(y1) ||
        typeof y2 !== 'number' || isNaN(y2) || !isFinite(y2)) {
      return null
    }
    
    if (x2 === x1) return y1
    
    // Linear extrapolation below minimum
    return y1 + (y2 - y1) * (xi - x1) / (x2 - x1)
  }
  
  // Within bounds: normal interpolation
  // Find surrounding points
  let i = 0
  for (i = 0; i < x.length - 1; i++) {
    if ((x[i] <= xi && xi <= x[i + 1]) || (x[i] >= xi && xi >= x[i + 1])) {
      break
    }
  }
  
  // Linear interpolation formula: y = y1 + (y2 - y1) * (x - x1) / (x2 - x1)
  const x1 = x[i]
  const x2 = x[i + 1]
  const y1 = y[i]
  const y2 = y[i + 1]
  
  // Validate that all values are valid numbers
  if (typeof x1 !== 'number' || isNaN(x1) || !isFinite(x1) ||
      typeof x2 !== 'number' || isNaN(x2) || !isFinite(x2) ||
      typeof y1 !== 'number' || isNaN(y1) || !isFinite(y1) ||
      typeof y2 !== 'number' || isNaN(y2) || !isFinite(y2) ||
      typeof xi !== 'number' || isNaN(xi) || !isFinite(xi)) {
    return null
  }
  
  if (x2 === x1) return y1 // Avoid division by zero
  
  return y1 + (y2 - y1) * (xi - x1) / (x2 - x1)
}

/**
 * Calculate achievable energy/power or capacity/current at user requirements using DCC curves
 * 
 * This function implements the MATLAB logic with domain support:
 * 1. Filter DCC data for 25°C ambient temperature (298.15K)
 * 2. Select X/Y axes based on domain ('energy-power' or 'capacity-current')
 * 3. Normalize by cell mass (gravimetric) or volume (volumetric)
 * 4. For given X requirement: interpolate achievable Y from X_DCC → Y_DCC curve
 * 5. For given Y requirement: interpolate achievable X from Y_DCC → X_DCC curve
 * 6. Only return cells where both interpolations are valid (no extrapolation needed)
 * 
 * @param {Object[]} cells - Array of cell objects with normalized data structure
 * @param {number} requiredX - User's X-axis requirement (Power in W or Current in A)
 * @param {number} requiredY - User's Y-axis requirement (Energy in Wh or Capacity in Ah)
 * @param {string} basis - 'abs' (absolute), 'grav' (gravimetric), or 'vol' (volumetric)
 * @param {string} domain - 'energy-power' (default) or 'capacity-current'
 * @returns {Object[]} Array of cells with calculated achievable_x and achievable_y
 * 
 * Returns structure:
 * [{
 *   cell: <original cell object>,
 *   achievable_y: <number> Y value at required X (energy/capacity, same unit as requiredY),
 *   achievable_x: <number> X value at required Y (power/current, same unit as requiredX),
 *   meets_requirements: <boolean> True if both interpolations succeeded
 * }]
 */
export function calculateRequirements(cells, requiredX, requiredY, basis = 'grav', domain = 'energy-power') {
  const results = []
  let debugCounter = 0
  
  // Statistics for debugging and UI feedback
  const stats = {
    total: cells.length,
    noDccData: 0,
    no25CData: 0,
    lessThan2Points: 0,
    noMassData: 0,
    noVolumeData: 0,
    interpolationFailed: 0,
    success: 0,
    usedFallbackTemperature: 0,
    availableTemperatures: new Set()
  }
  
  for (const cell of cells) {
    // Extract DCC arrays (already normalized in dataService)
    // Support both flat (electrical_T_amb_DCC) and nested (electrical.T_amb_DCC) structures
    // Flat structure is used by getCellNormalized() for 2D/3D plots (matches DimensionView.jsx)
    const T_amb_DCC = cell.electrical_T_amb_DCC || cell.electrical?.T_amb_DCC || []
    
    // Select arrays based on domain
    let X_DCC, Y_DCC
    if (domain === 'capacity-current') {
      // Current-Capacity domain
      X_DCC = cell.electrical_I_DCC || cell.electrical?.I_DCC || [] // Current in A
      Y_DCC = cell.electrical_C_DCC || cell.electrical?.C_DCC || [] // Capacity in Ah
    } else {
      // Power-Energy domain (default)
      X_DCC = cell.electrical_P_DCC || cell.electrical?.P_DCC || [] // Power in W
      Y_DCC = cell.electrical_W_DCC || cell.electrical?.W_DCC || [] // Energy in Wh
    }
    
    // Debug first cell with DCC data
    if (debugCounter === 0 && T_amb_DCC.length > 0) {
      console.log('[RequirementCalc] First cell DCC data sample:', {
        cellName: `${cell.manufacturer} ${cell.name}`,
        domain: domain,
        T_amb_DCC_sample: T_amb_DCC.slice(0, 5),
        T_amb_DCC_full: T_amb_DCC,
        X_DCC_sample: X_DCC.slice(0, 5),
        Y_DCC_sample: Y_DCC.slice(0, 5),
        lengths: { T: T_amb_DCC.length, X: X_DCC.length, Y: Y_DCC.length }
      })
      debugCounter++
    }
    
    // Skip if no DCC data
    if (T_amb_DCC.length === 0 || X_DCC.length === 0 || Y_DCC.length === 0) {
      stats.noDccData++
      continue
    }
    
    // Filter for 25°C ambient temperature (already converted from 298.15K to 25°C by dataService)
    // Temperature tolerance: ±0.5°C to account for measurement variations
    const T_target = 25 // °C
    const T_tolerance = 0.5 // ±0.5°C
    const indices_25C = []
    
    for (let i = 0; i < T_amb_DCC.length; i++) {
      const T_current = T_amb_DCC[i]
      if (Math.abs(T_current - T_target) < T_tolerance) {
        indices_25C.push(i)
      }
    }
    
    // Debug: show temperature range if no 25°C found
    if (indices_25C.length === 0) {
      stats.no25CData++
      const temps = [...T_amb_DCC].sort((a, b) => a - b)
      const uniqueTemps = [...new Set(T_amb_DCC.map(t => Math.round(t * 10) / 10))] // Round to 0.1°C
      console.log(`[RequirementCalc] ❌ No 25°C data for ${cell.manufacturer} ${cell.name}:`, {
        temperatures: T_amb_DCC,
        min: temps[0]?.toFixed(1),
        max: temps[temps.length - 1]?.toFixed(1),
        unique: uniqueTemps.map(t => `${t.toFixed(1)}°C`).join(', '),
        count: T_amb_DCC.length
      })
    }
    
    // Fallback: if no/insufficient 25°C data, try closest available temperature
    let workingIndices = indices_25C

    if (indices_25C.length < 2) {
      const uniqueTemps = [...new Set(T_amb_DCC.map(t => Math.round(t * 10) / 10))]
      uniqueTemps.forEach(t => stats.availableTemperatures.add(t))

      if (uniqueTemps.length > 0) {
        // Find closest temperature to 25°C
        const closestTemp = uniqueTemps.reduce((prev, curr) =>
          Math.abs(curr - T_target) < Math.abs(prev - T_target) ? curr : prev
        )

        // Collect indices for closest temperature
        const fallbackIndices = []
        for (let i = 0; i < T_amb_DCC.length; i++) {
          if (Math.abs(T_amb_DCC[i] - closestTemp) < T_tolerance) fallbackIndices.push(i)
        }

        if (fallbackIndices.length >= 2) {
          workingIndices = fallbackIndices
          stats.usedFallbackTemperature++
        } else {
          if (indices_25C.length === 1) stats.lessThan2Points++
          continue // Not enough data points at any temperature
        }
      } else {
        if (indices_25C.length === 1) stats.lessThan2Points++
        continue // No temperature data available
      }
    }

    // Extract temperature-filtered data
    const X_DCC_25C = workingIndices.map(i => X_DCC[i])
    const Y_DCC_25C = workingIndices.map(i => Y_DCC[i])
    
    // Filter out invalid (non-numeric) values
    const validIndices = []
    for (let j = 0; j < X_DCC_25C.length; j++) {
      const xVal = X_DCC_25C[j]
      const yVal = Y_DCC_25C[j]
      if (typeof xVal === 'number' && !isNaN(xVal) && isFinite(xVal) &&
          typeof yVal === 'number' && !isNaN(yVal) && isFinite(yVal)) {
        validIndices.push(j)
      }
    }
    
    if (validIndices.length < 2) {
      stats.lessThan2Points++
      continue // Need at least 2 valid points for interpolation
    }
    
    // Use only valid data points
    const X_DCC_valid = validIndices.map(j => X_DCC_25C[j])
    const Y_DCC_valid = validIndices.map(j => Y_DCC_25C[j])
    
    // Normalize by mass or volume
    let X_normalized = [...X_DCC_valid]
    let Y_normalized = [...Y_DCC_valid]
    
    if (basis === 'grav') {
      // Gravimetric (per kg)
      // Support: 1) normalized (mechanical.mass_g), 2) raw API (mechanical.m_cell kg), 3) minimal cache (mass_g flat)
      const mass_g = cell.mechanical?.mass_g || (cell.mechanical?.m_cell ? cell.mechanical.m_cell * 1000 : (cell.mass_g || 0))
      const m_cell_kg = mass_g / 1000
      
      if (m_cell_kg === 0) {
        stats.noMassData++
        console.log(`[RequirementCalc] ⚠️  Cell "${cell.manufacturer} ${cell.name}" skipped: no mass data (grav basis)`, {
          hasNormalizedNested: !!cell.mechanical?.mass_g,
          hasRawAPI: !!cell.mechanical?.m_cell,
          hasFlatCache: !!cell.mass_g,
          mechanical: cell.mechanical
        })
        continue
      }
      
      X_normalized = X_DCC_valid.map(x => x / m_cell_kg) // W/kg or A/kg
      Y_normalized = Y_DCC_valid.map(y => y / m_cell_kg) // Wh/kg or Ah/kg
      
    } else if (basis === 'vol') {
      // Volumetric (per L)
      // Support: 1) normalized (mechanical.vol_cell_cm3), 2) raw API (mechanical.vol_cell m³), 3) minimal cache (vol_cell_cm3 flat)
      const vol_cm3 = cell.mechanical?.vol_cell_cm3 || (cell.mechanical?.vol_cell ? cell.mechanical.vol_cell * 1e6 : (cell.vol_cell_cm3 || 0))
      const vol_cell_L = vol_cm3 / 1000
      
      if (vol_cell_L === 0) {
        stats.noVolumeData++
        console.log(`[RequirementCalc] ⚠️  Cell "${cell.manufacturer} ${cell.name}" skipped: no volume data (vol basis)`, {
          hasNormalizedNested: !!cell.mechanical?.vol_cell_cm3,
          hasRawAPI: !!cell.mechanical?.vol_cell,
          hasFlatCache: !!cell.vol_cell_cm3,
          mechanical: cell.mechanical
        })
        continue
      }
      
      X_normalized = X_DCC_valid.map(x => x / vol_cell_L) // W/L or A/L
      Y_normalized = Y_DCC_valid.map(y => y / vol_cell_L) // Wh/L or Ah/L
    }
    // else: basis === 'abs', use absolute values (no normalization)
    
    // Interpolation 1: At required X, what Y is achievable?
    // interp1(X_DCC, Y_DCC, required_x)
    // MATLAB logic: Allow downward extrapolation (when requiredX < min(X_normalized))
    const xMin = Math.min(...X_normalized)
    const xMax = Math.max(...X_normalized)
    const allowDownwardX = requiredX < xMin
    const achievable_y = linearInterpolate(X_normalized, Y_normalized, requiredX, allowDownwardX)
    
    // Interpolation 2: At required Y, what X is achievable?
    // interp1(Y_DCC, X_DCC, required_y)
    // MATLAB logic: Allow downward extrapolation (when requiredY < min(Y_normalized))
    const yMin = Math.min(...Y_normalized)
    const yMax = Math.max(...Y_normalized)
    const allowDownwardY = requiredY < yMin
    const achievable_x = linearInterpolate(Y_normalized, X_normalized, requiredY, allowDownwardY)
    
    // Debug first few cells
    if (debugCounter < 3) {
      console.log(`[RequirementCalc] Cell "${cell.manufacturer} ${cell.name}" interpolation:`, {
        requiredX, requiredY,
        xRange: `${xMin.toFixed(1)} - ${xMax.toFixed(1)}`,
        yRange: `${yMin.toFixed(1)} - ${yMax.toFixed(1)}`,
        allowDownwardX, allowDownwardY,
        achievable_y, achievable_x,
        withinXBounds: requiredX >= xMin && requiredX <= xMax,
        withinYBounds: requiredY >= yMin && requiredY <= yMax
      })
      debugCounter++
    }
    
    // Only include cells where BOTH interpolations are valid (no extrapolation)
    if (achievable_y !== null && achievable_x !== null) {
      stats.success++
      results.push({
        cell: cell,
        achievable_y: achievable_y,
        achievable_x: achievable_x,
        achievable_energy: achievable_y, // Backward compatibility
        achievable_power: achievable_x,  // Backward compatibility
        meets_requirements: achievable_y >= requiredY && achievable_x >= requiredX
      })
    } else {
      stats.interpolationFailed++
    }
  }
  
  // Log statistics summary
  console.log(`[RequirementCalc] 📊 Processing summary (basis=${basis}, domain=${domain}):`)
  console.log(`[RequirementCalc]   total: ${stats.total}, success: ${stats.success} (${((stats.success / stats.total) * 100).toFixed(1)}%)`)
  console.log(`[RequirementCalc]   skipped: noDccData=${stats.noDccData}, no25CData=${stats.no25CData}, lessThan2Points=${stats.lessThan2Points}`)
  console.log(`[RequirementCalc]   skipped: noMassData=${stats.noMassData}, noVolumeData=${stats.noVolumeData}, interpolationFailed=${stats.interpolationFailed}`)
  
  return { results, stats }
}

/**
 * Check if a cell meets simple threshold requirements (fast check without DCC interpolation)
 * Used for quick filtering before expensive DCC calculations
 * 
 * @param {Object} cell - Cell object
 * @param {number} minEnergy - Minimum energy requirement
 * @param {number} minPower - Minimum power requirement
 * @param {string} basis - 'abs', 'grav', or 'vol'
 * @returns {boolean} True if cell's peak values meet requirements
 */
export function meetsSimpleRequirements(cell, minEnergy, minPower, basis = 'grav') {
  let energy = 0
  let power = 0
  
  if (basis === 'abs') {
    energy = cell.electrical?.E_c10_Wh || 0
    power = cell.electrical?.P_peak_W || 0
  } else if (basis === 'grav') {
    energy = cell.electrical?.E_grav_Wh_per_kg || 0
    power = cell.electrical?.P_grav_W_per_kg || 0
  } else if (basis === 'vol') {
    energy = cell.electrical?.E_vol_Wh_per_L || 0
    power = cell.electrical?.P_vol_W_per_L || 0
  }
  
  return energy >= minEnergy && power >= minPower
}
